import { Field, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import { BaseEntity } from '../base/BaseEntity'
import { generateEntityId } from '../generateEntityId'
import type { DocumentLinkStatusEnum } from './DocumentLinkStatusEnum'
import { DocumentLinkTypeEnum } from './DocumentLinkTypeEnum'

/**
 * A public, expiring, revocable link to one document. `tokenHash` is the only persisted form of
 * the token; the raw value exists only in the shared URL and is never written to the database.
 * `documentNumber` and `createdByName` are snapshots taken at creation.
 */
@ObjectType()
@InputType('DocumentLinkInput')
@modelOptions({ schemaOptions: { timestamps: true } })
export class DocumentLink extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('dlnk') })
  declare _id: string

  // SHA-256 hash of the raw token. No `@Field` — never exposed through GraphQL.
  @Prop({ type: String, required: true })
  tokenHash: string

  @Field(() => DocumentLinkTypeEnum)
  @Prop({ type: String, required: true })
  documentType: DocumentLinkTypeEnum

  @Field(() => String)
  @Prop({ type: String, required: true })
  documentId: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  documentNumber?: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  createdByName?: string

  @Field(() => Date)
  @Prop({ type: Date, required: true })
  expiresAt: Date

  @Field(() => Date, { nullable: true })
  @Prop({ type: Date })
  revokedAt?: Date | null

  // The `status` @Field is inherited from `BaseEntity` and resolved by `DocumentLinkResolver` from `revokedAt` and
  // `expiresAt`; it is never stored. `declare` narrows its type to the enum without re-declaring the field.
  declare status?: DocumentLinkStatusEnum
}
